import MainLayout from '@/layouts/MainLayout.vue'
import { dejavooPocUiEnabled } from '@/config/dejavooPocUi'
import { ecomSettingsUiEnabled } from '@/config/ecomSettingsUi'
import { storeSettingsUiEnabled } from '@/config/storeSettingsUi'
import { schedulingUiEnabled } from '@/config/schedulingUi'

const ecomSettingsRoutes = ecomSettingsUiEnabled
  ? [
      {
        path: 'settings/ecom',
        name: 'settings-ecom',
        component: () => import('@/pages/EcomSettingsPage.vue'),
        meta: { requiresAuth: true, ability: 'settings.view', title: 'Online store' }
      }
    ]
  : []

const productStoreVariantRoutes = storeSettingsUiEnabled
  ? [
      {
        path: 'products/:productId/store-variants',
        name: 'product-store-variants',
        component: () => import('@/pages/products/ProductStoreVariantsPage.vue'),
        meta: { requiresAuth: true, ability: 'inventory.view', title: 'Store variants' }
      }
    ]
  : []

const storeSettingsRoutes = storeSettingsUiEnabled
  ? [
      {
        path: 'settings/store',
        name: 'settings-store',
        component: () => import('@/pages/StoreSettingsPage.vue'),
        meta: { requiresAuth: true, ability: 'settings.view', title: 'Blu Store' }
      },
      {
        path: 'settings/store/builder',
        name: 'settings-store-builder',
        component: () => import('@/pages/StoreBuilderPage.vue'),
        meta: { requiresAuth: true, ability: 'settings.view', title: 'Store builder', builderPageType: 'home' }
      },
      {
        path: 'settings/store/builder/product',
        name: 'settings-store-builder-product',
        component: () => import('@/pages/StoreBuilderPage.vue'),
        meta: { requiresAuth: true, ability: 'settings.view', title: 'Product template builder', builderPageType: 'product_template' }
      },
      {
        path: 'settings/store/builder/pages',
        name: 'settings-store-builder-pages',
        component: () => import('@/pages/StoreBuilderPagesPage.vue'),
        meta: { requiresAuth: true, ability: 'settings.view', title: 'Store content pages' }
      },
      {
        path: 'settings/store/builder/pages/:slug',
        name: 'settings-store-builder-content',
        component: () => import('@/pages/StoreBuilderPage.vue'),
        meta: { requiresAuth: true, ability: 'settings.view', title: 'Content page builder', builderPageType: 'content' }
      }
    ]
  : []

const dejavooPocRoutes = dejavooPocUiEnabled
  ? [
      {
        path: 'dev/dejavoo',
        name: 'dev-dejavoo-poc',
        component: () => import('@/pages/DejavooPoCPage.vue'),
        meta: { requiresAuth: true, ability: 'payments.view', title: 'Dejavoo PoC' }
      }
    ]
  : []

const bookingRoutes = [
  {
    path: 'booking',
    name: 'booking',
    component: () => import('@/pages/booking/BookingPage.vue'),
    meta: { requiresAuth: true, ability: 'scheduling.view', requiresBooking: true, title: 'Booking' }
  }
]

const bookingSettingsRoutes = [
  {
    path: 'settings/booking',
    name: 'settings-booking',
    component: () => import('@/pages/booking/BookingSettingsPage.vue'),
    meta: { requiresAuth: true, ability: 'scheduling.manage', requiresBooking: true, title: 'Booking Settings' }
  },
  {
    path: 'settings/booking/general',
    name: 'settings-booking-general',
    component: () => import('@/pages/booking/BookingSettingsGeneralPage.vue'),
    meta: { requiresAuth: true, ability: 'scheduling.manage', requiresBooking: true, title: 'Booking General Settings' }
  },
  {
    path: 'settings/booking/services',
    name: 'settings-booking-services',
    component: () => import('@/pages/booking/BookingSettingsServicesPage.vue'),
    meta: { requiresAuth: true, ability: 'scheduling.manage', requiresBooking: true, title: 'Booking Services' }
  },
  {
    path: 'settings/booking/fleet',
    name: 'settings-booking-fleet',
    component: () => import('@/pages/booking/BookingSettingsFleetPage.vue'),
    meta: { requiresAuth: true, ability: 'scheduling.manage', requiresBooking: true, title: 'Fleet Capacity' }
  },
  {
    path: 'booking/settings',
    redirect: { name: 'settings-booking' }
  }
]

const schedulingRoutes = schedulingUiEnabled
  ? [
      {
        path: 'scheduling',
        name: 'scheduling',
        component: () => import('@/pages/scheduling/SchedulingPage.vue'),
        meta: { requiresAuth: true, ability: 'scheduling.view', requiresScheduling: true, title: 'Scheduling' }
      }
    ]
  : []

const schedulingSettingsRoutes = schedulingUiEnabled
  ? [
      {
        path: 'settings/scheduling',
        name: 'settings-scheduling',
        component: () => import('@/pages/scheduling/SchedulingSettingsPage.vue'),
        meta: { requiresAuth: true, ability: 'scheduling.manage', requiresScheduling: true, title: 'Scheduling Settings' }
      },
      {
        path: 'scheduling/settings',
        redirect: { name: 'settings-scheduling' }
      }
    ]
  : []

const routes = [
  {
    path: '/',
    redirect: '/login'
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/pages/LoginPage.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/set-password',
    name: 'set-password',
    component: () => import('@/pages/SetPasswordPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/pages/ForgotPasswordPage.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/pages/ResetPasswordPage.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/customer-display',
    name: 'customer-display-mirror',
    component: () => import('@/pages/CustomerDisplayMirrorPage.vue'),
    meta: { requiresAuth: true, customerDisplayMirror: true, title: 'Customer display' }
  },
  {
    path: '/',
    component: MainLayout,
    children: [
      {
        path: 'settings',
        name: 'settings',
        component: () => import('@/pages/SettingsPage.vue'),
        meta: { requiresAuth: true, ability: 'settings.view' }
      },
      {
        path: 'groups',
        name: 'groups',
        component: () => import('@/pages/groups/GroupsPage.vue'),
        meta: { requiresAuth: true, ability: 'customers.view' }
      },
      {
        path: 'groups/:id',
        name: 'group-detail',
        component: () => import('@/pages/groups/GroupDetail.vue'),
        meta: { requiresAuth: true, ability: 'customers.view', title: 'Group Details' }
      },
      {
        path: 'tabs',
        name: 'tabs',
        component: () => import('@/pages/tabs/OpenTabsReport.vue'),
        meta: { requiresAuth: true, ability: 'ar.view' }
      },
      {
        path: 'tabs/:id',
        name: 'tab-detail',
        component: () => import('@/pages/tabs/TabDetail.vue'),
        meta: { requiresAuth: true, ability: 'ar.view', title: 'Tab Details' }
      },
      {
        path: 'rentals',
        name: 'rentals',
        component: () => import('@/pages/rentals/RentalsPage.vue'),
        meta: { requiresAuth: true, ability: 'rentals.view', title: 'Rentals' }
      },
      {
        path: 'settings/rentals',
        name: 'settings-rentals',
        component: () => import('@/pages/rentals/RentalSettingsPage.vue'),
        meta: { requiresAuth: true, ability: 'rentals.manage', title: 'Rental Settings' }
      },
      {
        path: 'rentals/settings',
        redirect: { name: 'settings-rentals' }
      },
      {
        path: 'rentals/units/:id',
        name: 'rental-unit-detail',
        component: () => import('@/pages/rentals/RentalUnitDetailPage.vue'),
        meta: { requiresAuth: true, ability: 'rentals.view', title: 'Rental Unit' }
      },
      {
        path: 'rentals/agreements/:id',
        name: 'rental-agreement-detail',
        component: () => import('@/pages/rentals/RentalAgreementDetailPage.vue'),
        meta: { requiresAuth: true, ability: 'rentals.view', title: 'Rental Agreement' }
      },
      {
        path: 'events',
        name: 'events',
        component: () => import('@/pages/events/EventsListPage.vue'),
        meta: { requiresAuth: true, ability: 'events.view' }
      },
      {
        path: 'events/new',
        name: 'events-new',
        component: () => import('@/pages/events/EventFormPage.vue'),
        meta: { requiresAuth: true, ability: 'events.manage', title: 'New Event' }
      },
      {
        path: 'events/:id',
        name: 'events-detail',
        component: () => import('@/pages/events/EventDetailPage.vue'),
        meta: { requiresAuth: true, ability: 'events.view', title: 'Event Details' }
      },
      {
        path: 'events/:id/edit',
        name: 'events-edit',
        component: () => import('@/pages/events/EventFormPage.vue'),
        meta: { requiresAuth: true, ability: 'events.manage', title: 'Edit Event' }
      },
      ...bookingRoutes,
      ...schedulingRoutes,
      ...bookingSettingsRoutes,
      ...schedulingSettingsRoutes,
      {
        path: 'settings/integrations',
        name: 'integrations',
        component: () => import('@/pages/IntegrationsPage.vue'),
        meta: { requiresAuth: true, ability: 'settings.view', title: 'Integrations' }
      },
      {
        path: 'settings/integrations/astro',
        name: 'astro-config',
        component: () => import('@/pages/AstroConfigPage.vue'),
        meta: { requiresAuth: true, ability: 'settings.view', title: 'Astro Config' }
      },
      {
        path: 'settings/integrations/justtcg',
        name: 'justtcg-config',
        component: () => import('@/pages/JustTCGConfigPage.vue'),
        meta: { requiresAuth: true, ability: 'settings.view', title: 'JustTCG Config' }
      },
      {
        path: 'settings/staff',
        name: 'settings-staff',
        component: () => import('@/pages/settings/StaffDirectoryPage.vue'),
        meta: { requiresAuth: true, ability: 'team.view', title: 'Staff' }
      },
      {
        path: 'settings/users',
        name: 'settings-users',
        component: () => import('@/pages/AdminUsersPage.vue'),
        meta: { requiresAuth: true, ability: 'team.view', title: 'Users' }
      },
      {
        path: 'settings/business-info',
        name: 'settings-business-info',
        component: () => import('@/pages/BusinessInfoPage.vue'),
        meta: { requiresAuth: true, ability: 'settings.view', title: 'Business Info' }
      },
      {
        path: 'settings/taxes',
        name: 'settings-taxes',
        component: () => import('@/pages/TaxSettingsPage.vue'),
        meta: { requiresAuth: true, ability: 'settings.view', title: 'Tax Settings' }
      },
      {
        path: 'settings/loyalty',
        name: 'settings-loyalty',
        component: () => import('@/pages/LoyaltySettingsPage.vue'),
        meta: { requiresAuth: true, ability: 'settings.view', title: 'Loyalty Settings' }
      },
      {
        path: 'settings/tabs',
        name: 'settings-tabs',
        component: () => import('@/pages/TabsSettingsPage.vue'),
        meta: { requiresAuth: true, ability: 'settings.view', title: 'Tabs Settings' }
      },
      {
        path: 'settings/invoices',
        name: 'settings-invoices',
        component: () => import('@/pages/InvoicesSettingsPage.vue'),
        meta: { requiresAuth: true, ability: 'settings.view', requiresInvoices: true, title: 'Invoices Settings' }
      },
      ...ecomSettingsRoutes,
      ...storeSettingsRoutes,
      {
        path: 'settings/payments',
        name: 'settings-payments',
        component: () => import('@/pages/PaymentProcessingPage.vue'),
        meta: { requiresAuth: true, ability: 'payments.view', title: 'Payment Processing' }
      },
      {
        path: 'settings/payments/mx',
        name: 'payments-mx',
        component: () => import('@/pages/MxPaymentsPage.vue'),
        meta: { requiresAuth: true, ability: 'payments.view', title: 'MX Payments' }
      },
      {
        path: 'settings/payments/spin',
        redirect: { name: 'payments-dejavoo' }
      },
      {
        path: 'settings/payments/stripe',
        name: 'payments-stripe-connect',
        component: () => import('@/pages/StripeConnectSetupPage.vue'),
        meta: { requiresAuth: true, ability: 'payments.view', title: 'Stripe Payments' }
      },
      {
        path: 'settings/payments/dejavoo',
        name: 'payments-dejavoo',
        component: () => import('@/pages/SpinPaymentsPage.vue'),
        meta: { requiresAuth: true, ability: 'payments.view', title: 'Dejavoo Payments' }
      },
      {
        path: 'settings/payments/iphone-tap-to-pay',
        name: 'settings-iphone-tap-to-pay',
        component: () => import('@/pages/settings/IphoneTapToPaySetupPage.vue'),
        meta: { requiresAuth: true, ability: 'payments.iphone_top_setup', title: 'Tap to Pay on iPhone' }
      },
      {
        path: 'settings/equipment',
        name: 'settings-equipment',
        component: () => import('@/pages/EquipmentSettingsPage.vue'),
        meta: { requiresAuth: true, ability: 'payments.view', title: 'Equipment' }
      },
      {
        path: 'settings/equipment/terminals',
        name: 'terminals',
        component: () => import('@/pages/TerminalManagementPage.vue'),
        meta: { requiresAuth: true, ability: 'payments.view', title: 'Terminal Management' }
      },
      {
        path: 'settings/equipment/kds',
        name: 'settings-equipment-kds',
        component: () => import('@/pages/KdsEquipmentPage.vue'),
        meta: { requiresAuth: true, ability: 'payments.view', title: 'Kitchen Displays' }
      },
      {
        path: 'settings/equipment/customer-display',
        name: 'settings-equipment-customer-display',
        component: () => import('@/pages/CustomerDisplayEquipmentPage.vue'),
        meta: { requiresAuth: true, ability: 'payments.view', title: 'Customer display' }
      },
      {
        path: 'settings/equipment/receipt-printer',
        name: 'settings-equipment-receipt-printer',
        component: () => import('@/pages/ReceiptPrinterSettingsPage.vue'),
        meta: { requiresAuth: true, ability: 'payments.view', title: 'Receipt printer' }
      },
      {
        path: 'kitchen',
        name: 'kitchen-queue',
        component: () => import('@/pages/KdsQueuePage.vue'),
        meta: { requiresAuth: true, ability: 'pos.sell', title: 'Kitchen Queue' }
      },
      {
        path: 'store-orders',
        name: 'store-order-queue',
        component: () => import('@/pages/StoreOrderQueuePage.vue'),
        meta: { requiresAuth: true, ability: 'pos.sell', title: 'Store pickup queue' }
      },
      ...dejavooPocRoutes,
      {
        path: 'settings/intake-form',
        name: 'settings-intake-form',
        component: () => import('@/pages/IntakeFormSettingsPage.vue'),
        meta: { requiresAuth: true, ability: 'settings.view', title: 'Intake Form' }
      },
      {
        path: 'settings/profile',
        name: 'settings-profile',
        component: () => import('@/pages/ProfilePage.vue'),
        meta: { requiresAuth: true, title: 'Profile' }
      },
      {
        path: 'leads',
        name: 'leads',
        component: () => import('@/pages/LeadsPage.vue'),
        meta: { requiresAuth: true, ability: 'customers.view', title: 'Leads' }
      },
      {
        path: '',
        redirect: '/dashboard'
      },
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('@/pages/DashboardPage.vue'),
        meta: { requiresAuth: true, ability: 'reports.view' }
      },
      {
        path: 'products',
        name: 'products',
        component: () => import('@/pages/ProductsPage.vue'),
        meta: { requiresAuth: true, ability: 'inventory.view' }
      },
      ...productStoreVariantRoutes,
      {
        path: 'inventory/setup',
        name: 'inventory-setup',
        component: () => import('@/pages/InventorySetupPage.vue'),
        meta: { requiresAuth: true, ability: 'inventory.manage', title: 'Inventory Setup' }
      },
      {
        path: 'categories',
        name: 'categories',
        component: () => import('@/pages/CategoriesPage.vue'),
        meta: { requiresAuth: true, ability: 'inventory.manage' }
      },
      {
        path: 'modifiers',
        name: 'modifiers',
        component: () => import('@/pages/ModifiersPage.vue'),
        meta: { requiresAuth: true, ability: 'inventory.manage' }
      },
      {
        path: 'inventory-logs',
        name: 'inventory-logs',
        component: () => import('@/pages/InventoryLogsPage.vue'),
        meta: { requiresAuth: true, ability: 'reports.view' }
      },
      {
        path: 'pricing-logs',
        name: 'pricing-logs',
        component: () => import('@/pages/PricingLogsPage.vue'),
        meta: { requiresAuth: true, ability: 'reports.view' }
      },
      {
        path: 'customers',
        name: 'customers',
        component: () => import('@/pages/CustomersPage.vue'),
        meta: { requiresAuth: true, ability: 'customers.view' }
      },
      {
        path: 'customers/:id',
        name: 'customer-profile',
        component: () => import('@/pages/customers/CustomerProfilePage.vue'),
        meta: { requiresAuth: true, ability: 'customers.view', title: 'Customer Profile' }
      },
      {
        path: 'reports/suspended-sales',
        name: 'suspended-sales-report',
        component: () => import('@/pages/reports/SuspendedSalesReport.vue'),
        meta: { requiresAuth: true, ability: 'reports.view', title: 'Suspended Sales' }
      },
      {
        path: 'sales',
        name: 'sales',
        component: () => import('@/pages/SalesPage.vue'),
        meta: { requiresAuth: true, ability: 'reports.view' }
      },
      {
        path: 'reports',
        name: 'reports',
        component: () => import('@/pages/ReportsPage.vue'),
        meta: { requiresAuth: true, ability: 'reports.view' }
      },

      {
        path: 'projects',
        name: 'projects',
        component: () => import('@/pages/projects/ProjectsPage.vue'),
        meta: { requiresAuth: true, ability: 'projects.view', requiresProjects: true }
      },
      {
        path: 'projects/new',
        name: 'project-new',
        component: () => import('@/pages/projects/ProjectEditorPage.vue'),
        meta: { requiresAuth: true, ability: 'projects.manage', requiresProjects: true, title: 'New Project' }
      },
      {
        path: 'projects/:id/edit',
        name: 'project-edit',
        component: () => import('@/pages/projects/ProjectEditorPage.vue'),
        meta: { requiresAuth: true, ability: 'projects.manage', requiresProjects: true, title: 'Edit Project' }
      },
      {
        path: 'projects/:id',
        name: 'project-view',
        component: () => import('@/pages/projects/ProjectDetailPage.vue'),
        meta: { requiresAuth: true, ability: 'projects.view', requiresProjects: true, title: 'Project' }
      },

      {
        path: 'invoices',
        name: 'invoices',
        component: () => import('@/pages/InvoicesPage.vue'),
        meta: { requiresAuth: true, ability: 'ar.view', requiresInvoices: true }
      },
      {
        path: 'invoices/recurring',
        name: 'invoices-recurring',
        component: () => import('@/pages/RecurringInvoicesPage.vue'),
        meta: { requiresAuth: true, ability: 'ar.view', requiresInvoices: true }
      },
      {
        path: 'invoices/new',
        name: 'invoice-new',
        component: () => import('@/pages/InvoiceEditorPage.vue'),
        meta: { requiresAuth: true, ability: 'ar.manage', requiresInvoices: true, title: 'New Invoice' }
      },
      {
        path: 'invoices/:id/edit',
        name: 'invoice-edit',
        component: () => import('@/pages/InvoiceEditorPage.vue'),
        meta: { requiresAuth: true, ability: 'ar.manage', requiresInvoices: true, title: 'Edit Invoice' }
      },
      {
        path: 'invoices/:id',
        name: 'invoice-view',
        component: () => import('@/pages/InvoiceViewPage.vue'),
        meta: { requiresAuth: true, ability: 'ar.view', requiresInvoices: true, title: 'Invoice' }
      },
      {
        path: 'settings/terms',
        redirect: { name: 'settings-invoices' }
      },
      {
        path: 'admin/users',
        redirect: '/settings/users'
      },
      {
        path: 'admin/roles',
        name: 'admin-roles',
        component: () => import('@/pages/AdminRolesPage.vue'),
        meta: { requiresAuth: true, ability: 'team.view', title: 'Roles' }
      },
      {
        path: 'business-info',
        redirect: '/settings/business-info'
      },
      {
        path: 'locations',
        name: 'locations',
        component: () => import('@/pages/LocationsPage.vue'),
        meta: { requiresAuth: true, ability: 'settings.view' }
      },
      {
        path: 'sales/register',
        name: 'sales-register',
        component: () => import('@/pages/SalesRegisterPage.vue'),
        meta: { requiresAuth: true, ability: 'pos.sell' }
      },
      {
        path: 'registers',
        name: 'registers',
        component: () => import('@/pages/RegisterManagementPage.vue'),
        meta: { requiresAuth: true, ability: 'pos.open-close-register' }
      },
      {
        path: 'terminals',
        redirect: '/settings/equipment/terminals'
      },
      {
        path: 'profile',
        redirect: '/settings/profile'
      }
    ]
  },

  {
    path: '/kds/setup',
    name: 'kds-setup',
    component: () => import('@/pages/KdsSetupPage.vue'),
    meta: { requiresAuth: false, title: 'Kitchen Display Setup' }
  },
  {
    path: '/kds',
    name: 'kds-queue',
    component: () => import('@/pages/KdsQueuePage.vue'),
    meta: { requiresAuth: false, title: 'Kitchen Display' }
  },
  {
    path: '/:catchAll(.*)*',
    component: () => import('@/pages/ErrorNotFound.vue')
  }
]

export default routes
